import { HTTP_OPTIONS, ROLES, TOWER_MENU_ITEMS } from '@silverfort-nodelibs/common/utils/consts';
import { Icons, Menu, MenuItem } from '@silverfort/sf-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LayoutData } from './types';
import {
    calcMonitorAlert,
    calcNotificationsAlert,
    calcSystemStatusAlert,
    CHECK_SESSION_INTERVAL,
    getLastAction,
    getMenuItemIdByPath,
    isUserPermitted,
    itemIdToUrl,
    MINUTES_FOR_LOGOUT_ALERT,
    MINUTES_UNTIL_AUTO_LOGOUT,
    REFRESH_LAYOUT_DATA_INTERVAL,
    setLastAction,
    stayOnPage
} from './TowerMenuUtils';
import ConfirmationMessage from '../components/Controls/ConfirmationMessage';
import { getUserState, httpGet } from '../components/utils';
import { useLocation } from 'react-router-dom';
import logout from '../hooks/logout';
import { useTowerLayout } from '../TowerLayout/TowerLayoutProvider';
import { useAppDrawer } from '../components/AppDrawer/AppDrawerProvider';
import { NHILogicalMenuItems } from '../modules/nhi/towerMenu/NHILogicalMenuItems';
import { ITDRLogicalMenuItems } from '../modules/itdr/ITDRLogicalMenuItems';
import { ISPMLogicalMenuItems } from '../modules/ispm/ISPMLogicalMenuItems';
import { AuthenticationFirewallMenuItems } from '../modules/authenticationFirewall/towerMenu/AuthenticationFirewallMenuItems';
import { UniversalMfaMenuItems } from '../modules/universalMFA/towerMenu/UniversalMFAMenuItems';
import { AiAgentSecurityLogicalMenuItems } from '../modules/aiAgentSecurity/components/towerMenu';
import { AIAssistantLogicalMenuItems } from '../modules/aiAssistant/towerMenu/AIAssistantLogicalMenuItems';
import { useIsCloudOnly } from '../components/SystemSettings/hooks/featureFlags/useIsCloudOnly';
import { MenuItemWrapper } from './logicalMenuItems/MenuItemWrapper';
import { AccessIntelligenceMenu } from '../modules/accessAnalysis/components/towerMenu';

export interface TowerMenuProps {
    selectedItem: string | undefined;
    onSelectedItemChange: (id: string) => void;
}

const TowerMenu: React.FC<TowerMenuProps> = ({ selectedItem, onSelectedItemChange }) => {
    const { hideTowerMenu, setIsOfflineMode } = useTowerLayout();
    const isMountedRef = useRef(true);
    const [pageVisitedSent, setPageVisitedSent] = useState(false);
    const [layoutData, setLayoutData] = useState<LayoutData | undefined>();
    const [userData, setUserData] = useState<{ user: UserData; csrf: string; sessionTimeout: number } | undefined>();
    const [showTimeoutModal, setShowTimeoutModal] = useState(false);
    const [lastActivity, setLastActivity] = useState<number>(Date.now());
    const location = useLocation();
    const { closeAppDrawer } = useAppDrawer();
    const isCloudOnly = useIsCloudOnly();

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const data = await getUserState();
                isMountedRef.current && setUserData(data);
            } catch (error) {
                console.error("failed getting the user's data from server!", error);
            }
        };

        const getLayoutData = async () => {
            await fetchUserData();

            try {
                const params: { pageId?: string | number; screenResolution?: string } = {};
                if (!pageVisitedSent) {
                    params.pageId = selectedItem;
                    if (window && window.screen) {
                        params.screenResolution = `${window.screen.width}x${window.screen.height}`;
                    }
                }

                const { data } = await httpGet(`/Data/getLayoutData`, {
                    [HTTP_OPTIONS.REQ_OPTIONS]: { params: params }
                });
                if (isMountedRef.current && data) {
                    setLayoutData({
                        maxAlarmLevel: (data as LayoutData).maxAlarmLevel,
                        disconnectedDcsLevel: (data as LayoutData).disconnectedDcsLevel,
                        disconnectedPushServicesLevel: (data as LayoutData).disconnectedPushServicesLevel,
                        nodesStatuses: (data as LayoutData).nodesStatuses,
                        offlineMode: (data as LayoutData).offlineMode
                    });
                    setIsOfflineMode((data as LayoutData).offlineMode);
                    setPageVisitedSent(true);
                }
            } catch (err) {
                console.error('Failed to load layout data: ', err);
            }
        };

        getLayoutData();
        setLastAction(Date.now);
        isMountedRef.current = true;

        const layoutDataIntervalId = setInterval(getLayoutData, REFRESH_LAYOUT_DATA_INTERVAL);

        return () => {
            clearInterval(layoutDataIntervalId);
            isMountedRef.current = false;
        };
    }, [selectedItem, pageVisitedSent]);

    useEffect(() => {
        return () => {
            closeAppDrawer();
        };
    }, [selectedItem]);

    useEffect(() => {
        const checkSessionTimeout = () => {
            const now = Date.now();
            const timeLeft =
                getLastAction() +
                (userData?.sessionTimeout
                    ? userData.sessionTimeout - 30 * 1000
                    : MINUTES_UNTIL_AUTO_LOGOUT * 60 * 1000);
            const diff = timeLeft - now;
            const isTimeout = diff < 0;
            if (isTimeout) {
                window.location.href = '/logout';
            } else if (diff < MINUTES_FOR_LOGOUT_ALERT * 60 * 1000 && !showTimeoutModal) {
                setShowTimeoutModal(true);
            }
        };

        const checkSessionIntervalId = setInterval(checkSessionTimeout, CHECK_SESSION_INTERVAL);

        return () => {
            clearInterval(checkSessionIntervalId);
        };
    }, [userData?.sessionTimeout, showTimeoutModal]);

    useEffect(() => {
        const resetSession = () => {
            const now = Date.now();

            if (!showTimeoutModal && now - 1000 > lastActivity) {
                setLastAction(now);
                isMountedRef.current && setLastActivity(now);
            }
        };

        document.body.addEventListener('click', resetSession);
        document.body.addEventListener('mouseover', resetSession);
        document.body.addEventListener('keypress', resetSession);

        return () => {
            document.body.removeEventListener('click', resetSession);
            document.body.removeEventListener('mouseover', resetSession);
            document.body.removeEventListener('keypress', resetSession);
        };
    }, [lastActivity, showTimeoutModal]);

    const handleMenuItemSelection = (id) => {
        onSelectedItemChange(id);
        window.location.href = itemIdToUrl[id] || '/';
    };
    const getTowerMenuHref = useCallback((id) => {
        return itemIdToUrl[id] || '/';
    }, []);

    useEffect(() => {
        const selected = getMenuItemIdByPath(location.pathname);
        onSelectedItemChange(selected || '');
    }, [location.pathname, onSelectedItemChange]);

    if (hideTowerMenu) {
        return null;
    }

    return (
        <>
            <Menu selectedItem={selectedItem} onHeaderClick={() => handleMenuItemSelection(TOWER_MENU_ITEMS.DASHBOARD)}>
                <MenuItem
                    disabled={!isUserPermitted(userData, 'dashboard', ROLES.VIEWER.value)}
                    href={getTowerMenuHref(TOWER_MENU_ITEMS.DASHBOARD)}
                    id={TOWER_MENU_ITEMS.DASHBOARD}
                    ItemIcon={Icons.HomePage}
                    title="Dashboard"
                />
                <MenuItemWrapper id={TOWER_MENU_ITEMS.INVENTORY} userData={userData?.user}>
                    <MenuItem
                        disabled={!isUserPermitted(userData, 'inventory', ROLES.VIEWER.value)}
                        href={getTowerMenuHref(TOWER_MENU_ITEMS.INVENTORY)}
                        id={TOWER_MENU_ITEMS.INVENTORY}
                        ItemIcon={Icons.User}
                        title="Identity Graph"
                    />
                </MenuItemWrapper>
                <MenuItemWrapper id={TOWER_MENU_ITEMS.PROTECTION_POLICIES} userData={userData?.user}>
                    <MenuItem
                        disabled={!isUserPermitted(userData, 'policies', ROLES.VIEWER.value)}
                        href={getTowerMenuHref(TOWER_MENU_ITEMS.PROTECTION_POLICIES)}
                        id={TOWER_MENU_ITEMS.PROTECTION_POLICIES}
                        ItemIcon={Icons.DocShield}
                        title="Policies"
                    />
                </MenuItemWrapper>
                <AuthenticationFirewallMenuItems
                    handleMenuItemSelection={handleMenuItemSelection}
                    getTowerMenuHref={getTowerMenuHref}
                />
                <UniversalMfaMenuItems
                    handleMenuItemSelection={handleMenuItemSelection}
                    getTowerMenuHref={getTowerMenuHref}
                    userData={userData}
                />
                <MenuItemWrapper id={TOWER_MENU_ITEMS.PA} userData={userData?.user}>
                    <MenuItem
                        disabled={!isUserPermitted(userData, 'privilegedAccounts', ROLES.VIEWER.value)}
                        href={getTowerMenuHref(TOWER_MENU_ITEMS.PA)}
                        id={TOWER_MENU_ITEMS.PA}
                        ItemIcon={Icons.DomainAdmin}
                        title="Privileged Access"
                    />
                </MenuItemWrapper>
                <NHILogicalMenuItems
                    handleMenuItemSelection={handleMenuItemSelection}
                    userData={userData}
                    getTowerMenuHref={getTowerMenuHref}
                />
                <ITDRLogicalMenuItems
                    handleMenuItemSelection={handleMenuItemSelection}
                    userData={userData}
                    getTowerMenuHref={getTowerMenuHref}
                />
                <ISPMLogicalMenuItems
                    handleMenuItemSelection={handleMenuItemSelection}
                    userData={userData}
                    getTowerMenuHref={getTowerMenuHref}
                />
                <AiAgentSecurityLogicalMenuItems
                    handleMenuItemSelection={handleMenuItemSelection}
                    getTowerMenuHref={getTowerMenuHref}
                    userData={userData}
                />
                <AIAssistantLogicalMenuItems
                    handleMenuItemSelection={handleMenuItemSelection}
                    getTowerMenuHref={getTowerMenuHref}
                    userData={userData}
                />
                <AccessIntelligenceMenu
                    handleMenuItemSelection={handleMenuItemSelection}
                    userData={userData}
                    getTowerMenuHref={getTowerMenuHref}
                />
                <MenuItemWrapper id={TOWER_MENU_ITEMS.APP_DISCOVERY} userData={userData?.user}>
                    <MenuItem
                        disabled={!isUserPermitted(userData, 'applicationsDiscovery', ROLES.VIEWER.value)}
                        href={getTowerMenuHref(TOWER_MENU_ITEMS.APP_DISCOVERY)}
                        id={TOWER_MENU_ITEMS.APP_DISCOVERY}
                        ItemIcon={Icons.AppWindow}
                        title="App Discovery"
                    />
                </MenuItemWrapper>
                <MenuItemWrapper id={TOWER_MENU_ITEMS.LOGS} userData={userData?.user}>
                    <MenuItem
                        disabled={!isUserPermitted(userData, 'logs', ROLES.VIEWER.value)}
                        href={getTowerMenuHref(TOWER_MENU_ITEMS.LOGS)}
                        id={TOWER_MENU_ITEMS.LOGS}
                        ItemIcon={Icons.Document}
                        title="Authentication Logs"
                    />
                </MenuItemWrapper>
                <MenuItem
                    status={calcMonitorAlert(layoutData, isCloudOnly)}
                    id={TOWER_MENU_ITEMS.MONITOR}
                    ItemIcon={Icons.Monitor}
                    title="Monitor">
                    <MenuItemWrapper id={TOWER_MENU_ITEMS.MONITOR_SYSTEM_STATUS} userData={userData?.user}>
                        <MenuItem
                            disabled={!isUserPermitted(userData, 'systemStatus', ROLES.VIEWER.value)}
                            href={getTowerMenuHref(TOWER_MENU_ITEMS.MONITOR_SYSTEM_STATUS)}
                            id={TOWER_MENU_ITEMS.MONITOR_SYSTEM_STATUS}
                            title="System Status"
                            status={calcSystemStatusAlert(layoutData, isCloudOnly)}
                        />
                    </MenuItemWrapper>
                    <MenuItem
                        disabled={!isUserPermitted(userData, 'notifications', ROLES.VIEWER.value)}
                        href={getTowerMenuHref(TOWER_MENU_ITEMS.MONITOR_NOTIFICATIONS)}
                        id={TOWER_MENU_ITEMS.MONITOR_NOTIFICATIONS}
                        title="System Events"
                        status={calcNotificationsAlert(layoutData)}
                    />
                    <MenuItemWrapper id={TOWER_MENU_ITEMS.MONITOR_SOFTWARE_UPDATES} userData={userData?.user}>
                        <MenuItem
                            disabled={!isUserPermitted(userData, 'softwareUpdates', ROLES.VIEWER.value)}
                            href={getTowerMenuHref(TOWER_MENU_ITEMS.MONITOR_SOFTWARE_UPDATES)}
                            id={TOWER_MENU_ITEMS.MONITOR_SOFTWARE_UPDATES}
                            title="Software Updates"
                        />
                    </MenuItemWrapper>
                </MenuItem>
                <MenuItem
                    disabled={!isUserPermitted(userData, 'settings', ROLES.VIEWER.value)}
                    href={getTowerMenuHref(TOWER_MENU_ITEMS.SETTINGS)}
                    bottom={true}
                    id={TOWER_MENU_ITEMS.SETTINGS}
                    ItemIcon={Icons.SettingsPage}
                    title="Settings"
                />
                <MenuItem
                    onClick={() => logout()}
                    bottom={true}
                    id={TOWER_MENU_ITEMS.LOGOUT}
                    ItemIcon={Icons.Logout}
                    title="Log Out"
                />
            </Menu>

            {showTimeoutModal && (
                <ConfirmationMessage
                    className="z-[10000]"
                    title={'Session Timeout'}
                    body={'Your session is about to expire.'}
                    approveText={'Continue Session'}
                    cancelText={'Logout'}
                    disableCloseButton={true}
                    OnCancel={() => {
                        window.location.href = '/logout';
                    }}
                    OnApprove={() => {
                        setShowTimeoutModal(false);
                        stayOnPage();
                    }}
                    disableHide
                />
            )}
        </>
    );
};

export default TowerMenu;
